/**
 * The Table Manager — one governed editor for the table-presentation document
 * (#1982 closure).
 *
 * ── Why this is a sibling of `LayoutComposer` and not a page of its own ─────
 * 62325 is explicit: *"the remaining Table Manager editor uses the same document
 * and customization ceremony; it must not create a second `LayoutDocument`
 * store."* Both halves of that sentence are load-bearing and they pull in
 * opposite directions, so it is worth saying which is which:
 *
 *   · the DOCUMENT is the one core already stores — `TablePresentationDocument`,
 *     validated by `validateTablePresentation` here and by
 *     `core.enforce_table_presentation` at the database boundary. This editor
 *     invents no shape, adds no field, and stores nothing;
 *   · the CEREMONY is `LayoutComposer`'s — a layer control, a live preview of
 *     the consequence, visible issues, and one `ActionBar` with Save / Cancel /
 *     Reset. Sharing the ceremony is what makes customization feel like one
 *     feature rather than five;
 *   · and a `LayoutDocument` appears NOWHERE below. A table's lenses are not
 *     sections and items, and modelling them as one to reuse the composer
 *     literally would have been the second store the ruling forbids, wearing
 *     the first one's clothes.
 *
 * ── It owns no persistence and no authority ─────────────────────────────────
 * The host saves through `core.set_table_presentation`, which is where the
 * authority check lives. This component cannot widen who may edit anything: it
 * is given a document and hands one back. That is the same division
 * `LayoutComposer` states in its own header, and it is why `onSave` takes a
 * document rather than an entity key.
 *
 * ── The one rule the editor enforces locally, and why ───────────────────────
 * Save is refused while `validateTablePresentation` returns anything. Not
 * because the client is the authority — the trigger is, and it will refuse the
 * same document — but because a Save that travels to the server, fails, and
 * comes back as a toast is a worse way to learn that the default is not among
 * the available lenses than a disabled button and the sentence saying so.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  GRAPH_LAYOUTS,
  RELATED_PRESENTATIONS,
  TABLE_PRESENTATIONS,
  WORKSPACE_MODES,
  DERIVED_QUICK_CREATE_COMMAND,
  canonicalTablePresentation,
  isBuiltinCommandAction,
  tablePresentationVersionFloor,
  validateTablePresentation,
  type GraphLayout,
  type RelatedOffer,
  type RelatedPresentation,
  type TableCardSpec,
  type TableCommand,
  type TableCommandBarDefault,
  type TableCommandKind,
  type TablePresentation,
  type TablePresentationDocument as GovernedTablePresentationDocument,
  type WorkspaceContract,
  type WorkspaceMode,
} from '@solum/experience'
import { Button } from '../controls/Button'
import { Field } from '../controls/Field'
import { Input } from '../controls/Input'
import { Select } from '../controls/Select'
import { SegmentedControl } from '../controls/SegmentedControl'
import { ActionBar } from '../work/ActionBar'
import s from './TablePresentationComposer.module.css'

/** One field of the entity, as the card recipe pickers offer it. */
export interface TableFieldChoice {
  key: string
  label: string
  /** Required at CREATE time only when the server does not fill it. */
  required?: boolean
  hasServerDefault?: boolean
}
/** Stable governed registry choices; labels are display only, keys go on wire. */
export interface TableReferenceChoice {
  key: string
  label: string
  /** Quick-create forms expose their governed field inventory. */
  fields?: readonly string[]
  /** Exactly one server-ordered form is the Automatic resolution candidate. */
  automatic?: boolean
}

/** Where this document came from, so an author knows what they are overriding. */
export interface TablePresentationProvenanceNote {
  scope?: string
  source?: 'declared' | 'fallback'
}

export interface TablePresentationComposerProps {
  value: TablePresentationDocument
  /** Stable identity of the layer/persona/host being edited. */
  targetKey?: string
  /** The entity's fields, for the card recipe. Empty is legal — the recipe is optional. */
  fields: readonly TableFieldChoice[]
  /** Governed `core.form` names available to quick-create. */
  forms?: readonly TableReferenceChoice[]
  /** Governed entity-action tool ids available on a card. */
  actions?: readonly TableReferenceChoice[]
  /**
   * Governed COLLECTION-level verbs available as commands (#2375): the
   * `core.entity_action` bindings of this entity with `cardinality = 'none'`.
   *
   * A separate list from `actions`, deliberately: those are per-record verbs a
   * card hangs on one row, these act on the collection and need no selection —
   * the database refuses a command naming anything else, so offering the card
   * list here would hand the author a choice the trigger refuses.
   */
  commandActions?: readonly TableReferenceChoice[]
  /** Whether the selected host has a real create/ghost execution seam. */
  quickCreateAvailable?: boolean
  /** Whether the selected host has a governed per-record action seam. */
  cardActionsAvailable?: boolean
  /**
   * Whether the selected host has the governed seam an entity-action COMMAND
   * needs (the console). The built-in commands need none of their own.
   */
  commandActionsAvailable?: boolean
  /**
   * Which lenses THIS deployment's hosts can actually draw, per lens.
   *
   * An already-stored unsupported lens remains visible so it can be removed,
   * but a host cannot newly offer one it cannot render. This is an authoring
   * inventory, not the runtime's defensive fallback.
   */
  supported?: readonly TablePresentation[]
  /**
   * The relationship SLOTS this entity registers, when it is being edited as a
   * SOURCE (#2276 owner correction, point 6).
   *
   * Presentation is authored per registered slot, not once for a record: two
   * slots reaching the same entity — billing addresses and sites — are two
   * questions and may have different answers. Empty or absent means this
   * surface is not authoring a source, and the per-relationship section is not
   * drawn at all rather than drawn empty.
   *
   * `mapCapable` comes from the TARGET's resolved document, because capability
   * is the far entity's property and this component resolves nothing.
   */
  slots?: readonly TableSlotChoice[]
  /**
   * THIS entity's own canonical form sections, by declared id — the choices a
   * Peek recipe selects from. Empty or absent means the entity resolves no form
   * and the preview section chooser is not drawn at all, rather than drawn with
   * nothing in it.
   */
  sections?: readonly { id: string; label: string }[]
  /** Which layer is being edited — "Tenant default", "My view". */
  layerLabel: string
  /** Where the document currently in `value` came from. */
  provenance?: TablePresentationProvenanceNote
  /** Server-side issues to show beside the local ones. */
  issues?: readonly string[]
  busy?: boolean
  /** The host's own layer picker, rendered into the toolbar. */
  layerControl?: ReactNode
  onSave: (document: TablePresentationDocument) => void
  onCancel: () => void
  /** Absent when there is nothing beneath this layer to fall back to. */
  onReset?: () => void
  testId?: string
}

/** One registered relationship slot, as an author sees it. */
export interface TableSlotChoice {
  /** `<relType>:<direction>:<otherEntity>` — the stable identity that is stored. */
  id: string
  /** The relationship as read FROM this record: "Addresses", "Billing address". */
  label: string
  /** The far entity this slot draws. STORED, never parsed back out of `id`. */
  target: string
  /** The far entity's own label, so an author can tell two slots apart. */
  targetLabel?: string
  /** Does the TARGET declare a coordinate recipe? Resolved by the host. */
  mapCapable?: boolean
  /** Does the TARGET resolve a canonical readable form? Resolved by the host. */
  peekCapable?: boolean
  /** The target's form sections, by declared id, for a Peek selection. */
  sections?: readonly { id: string; label: string }[]
}

const RELATED_LABEL: Record<RelatedPresentation, string> = {
  grid: 'Grid', cards: 'Cards', map: 'Map', peek: 'Peek', timeline: 'Timeline',
}

/**
 * The WHOLE-RECORD workspace's two vocabularies (#2324).
 *
 * `Metro` is in the second list and not the first, and that move is the whole
 * ruling: it does not describe how one relationship list is rendered, it
 * describes how the record's complete relationship network is laid out.
 */
const MODE_LABEL: Record<WorkspaceMode, string> = { list: 'List', graph: 'Graph' }
const LAYOUT_LABEL: Record<GraphLayout, string> = { levels: 'Levels', network: 'Network', metro: 'Metro' }

/** The contract an author starts from when they first turn the block on. */
const WORKSPACE_FLOOR: WorkspaceContract = { modes: ['list', 'graph'], defaultMode: 'list' }

const LENS_LABEL: Record<TablePresentation, string> = {
  grid: 'Grid',
  cards: 'Cards',
  // The storage token is `board`; #1987 already established that a host may
  // label it Kanban. The vocabulary stays `board` on the wire.
  board: 'Kanban',
  calendar: 'Calendar',
}

/* The command bar's two canonical values (#2375, ADR F-84 §3). It says only
   whether the governed command buttons sit beside the collection's mark —
   selection actions appear inline from the first selected row either way, so
   the old "Automatic — appears when rows are selected" option has nothing left
   to mean. A stored legacy `auto` is read as `shown` (the draft below is
   canonicalised on load), so saving never writes it back. */
const COMMAND_BAR: { value: TableCommandBarDefault; label: string }[] = [
  { value: 'shown', label: 'Show command buttons' },
  { value: 'hidden', label: 'Hide command buttons' },
]

const NONE = '—'

function normalizeRequiredQuickCreate(
  value: TablePresentationDocument,
  fields: readonly TableFieldChoice[],
  forms: readonly TableReferenceChoice[],
): TablePresentationDocument {
  if (!value.quickCreate) return value
  const selected = value.quickCreate.form
    ? forms.find((form) => form.key === value.quickCreate!.form)
    : forms.find((form) => form.automatic)
  const admitted = selected?.fields ? new Set(selected.fields) : null
  const required = fields
    .filter((field) => field.required && !field.hasServerDefault && (!admitted || admitted.has(field.key)))
    .map((field) => field.key)
  const missing = required.filter((key) => !value.quickCreate!.fields.includes(key))
  return missing.length === 0 ? value : {
    ...value, quickCreate: { ...value.quickCreate, fields: [...missing, ...value.quickCreate.fields] },
  }
}

export function TablePresentationComposer({
  value, targetKey = 'default', fields, forms = [], actions = [], commandActions = [], slots = [], sections = [], supported = TABLE_PRESENTATIONS, layerLabel, provenance,
  quickCreateAvailable = true, cardActionsAvailable = true, commandActionsAvailable = true,
  issues = [], busy = false, layerControl, onSave, onCancel, onReset,
  testId = 'table-presentation-composer',
}: TablePresentationComposerProps) {
  const [draft, setDraft] = useState<TablePresentationDocument>(() => normalizeRequiredQuickCreate(canonicalTablePresentation(value), fields, forms))
  const draftTarget = useRef(targetKey)
  const supportedSet = useMemo(() => new Set(supported), [supported])

  /* A target switch is a new editing address, not a prop update to one draft.
     Without this reset, changing Console/Sales to Portal/Delivery kept the
     first target's unsaved document and Save wrote it under the second key. */
  useEffect(() => {
    if (draftTarget.current === targetKey) return
    draftTarget.current = targetKey
    setDraft(normalizeRequiredQuickCreate(canonicalTablePresentation(value), fields, forms))
  }, [targetKey, value, fields, forms])

  const selectedQuickForm = draft.quickCreate?.form
    ? forms.find((form) => form.key === draft.quickCreate!.form)
    : forms.find((form) => form.automatic)
  const selectedQuickFields = selectedQuickForm?.fields ? new Set(selectedQuickForm.fields) : null

  /* The SAME validator the database boundary mirrors. Not a second rulebook —
     `core.enforce_table_presentation`'s header says it is deliberately at least
     as strict as this one, so an author cannot be told yes here and no there. */
  const localIssues = useMemo(() => validateTablePresentation(draft), [draft])
  const supportIssues = useMemo(() => [
    ...draft.availablePresentations
      .filter((kind) => !supportedSet.has(kind))
      .map((kind) => `${LENS_LABEL[kind]} is not available on this host`),
    ...(!quickCreateAvailable && draft.quickCreate ? ['Quick create is not available on this host'] : []),
    ...(!cardActionsAvailable && (draft.card?.actions?.length ?? 0) > 0
      ? ['Card actions are not available on this host'] : []),
    ...(draft.quickCreate && !selectedQuickForm
      ? ['Quick create has no governed form for this target'] : []),
    ...(draft.quickCreate && selectedQuickFields
      ? fields
          .filter((field) => field.required && !field.hasServerDefault && !selectedQuickFields.has(field.key))
          .map((field) => `${selectedQuickForm?.label ?? 'The selected form'} omits required create field ${field.label}`)
      : []),
    ...(draft.quickCreate && selectedQuickFields
      ? draft.quickCreate.fields
          .filter((key) => !selectedQuickFields.has(key))
          .map((key) => `${fields.find((field) => field.key === key)?.label ?? key} is not on the selected governed form`)
      : []),
    /* The catalogue half of the command rules, from the choices this editor was
       GIVEN — the same two refusals `core.enforce_table_presentation_references`
       makes, so an author learns them here rather than from a toast. */
    ...(!commandActionsAvailable && (draft.commands ?? []).some((c) => !isBuiltinCommandAction(c.action))
      ? ['Governed commands are not available on this host'] : []),
    ...(draft.commands ?? [])
      .filter((c) => !isBuiltinCommandAction(c.action) && !commandActions.some((a) => a.key === c.action))
      .map((c) => `${c.label || c.id} runs ${c.action}, which is not a governed collection command for this entity`),
  ], [cardActionsAvailable, commandActions, commandActionsAvailable, draft.availablePresentations, draft.card?.actions, draft.commands, draft.quickCreate, fields, quickCreateAvailable, selectedQuickFields, selectedQuickForm, supportedSet])
  const allIssues = useMemo(() => [...localIssues, ...supportIssues, ...issues], [localIssues, supportIssues, issues])
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(value), [draft, value])

  const patch = (next: Partial<TablePresentationDocument>) =>
    setDraft((d) => ({ ...d, ...next }))

  const toggleLens = (kind: TablePresentation, on: boolean) => setDraft((d) => {
    const available = on
      ? TABLE_PRESENTATIONS.filter((k) => k === kind || d.availablePresentations.includes(k))
      : d.availablePresentations.filter((k) => k !== kind)
    /* Removing the lens that WAS the default cannot leave the document naming a
       lens it no longer offers — the trigger refuses exactly that, so the
       editor repairs it rather than handing the author an error they did not
       cause. `grid` last, because it is the one every host can draw. */
    const defaultPresentation = available.includes(d.defaultPresentation)
      ? d.defaultPresentation
      : (available[0] ?? 'grid')
    return { ...d, availablePresentations: available, defaultPresentation }
  })

  /**
   * The RELATED offer — what a relationship workspace over this entity shows.
   *
   * A separate block from `availablePresentations` because they are two
   * different offers over two different surfaces: a board needs a group-by and
   * means nothing over a record's related groups, and a peek preview means
   * nothing over a standalone list. Editing one must not move the other.
   */
  /**
   * Which grammar version this document is in, AFTER the edit in hand.
   *
   * Every block used to set `version: 2` flatly, which was right while 2 was the
   * top of the grammar. It is not any more: a document carrying a workspace
   * contract is version 3, and an author editing its RELATED offer must not
   * silently push it back to 2 — the trigger refuses that, naming a block they
   * never touched.
   *
   * Read off the blocks the document CARRIES (`tablePresentationVersionFloor`)
   * rather than listed here: a hand-kept `workspace ? 3 : 2` is how a version-4
   * picker or a version-5 command list would be silently pushed back to 3.
   */
  const atVersion = (d: Partial<TablePresentationDocument>, floor: number) =>
    Math.max(floor, tablePresentationVersionFloor(d)) as TablePresentationDocument['version']
  const docVersion = (d: TablePresentationDocument) => atVersion(d, 2)

  const patchRelated = (next: Partial<RelatedOffer> | null) => setDraft((d) => {
    if (next === null) { const { related: _related, ...rest } = d; return { ...rest, version: docVersion(d) } }
    const base: RelatedOffer = d.related ?? { available: ['grid'], default: 'grid' }
    const merged = { ...base, ...next }
    // A default the offer no longer contains is exactly what the trigger
    // refuses, so the editor repairs it rather than handing the author an error
    // they did not cause — the same repair `toggleLens` makes above.
    const available = merged.available.length > 0 ? merged.available : (['grid'] as RelatedPresentation[])
    const fallback = available.includes('grid') ? 'grid' : available[0]!
    return {
      ...d,
      // Either version-2 block makes the document version 2, and the validator
      // in both places says so. Setting it here means an author never has to.
      version: docVersion(d),
      related: {
        ...merged,
        available,
        default: available.includes(merged.default) ? merged.default : fallback,
      },
    }
  })

  /**
   * Author ONE slot, leaving every sibling exactly as it was.
   *
   * `null` removes the slot's entry entirely, which is Reset: the slot goes back
   * to inheriting whatever the ladder answers for it — the source default, the
   * target's own, or the platform floor — rather than being pinned to a copy of
   * today's answer. Writing the inherited value instead would freeze it, and an
   * administrator changing the source default later would find one relationship
   * mysteriously ignoring them.
   */
  const patchSlot = (slot: TableSlotChoice, next: Partial<RelatedOffer> | null) => setDraft((d) => {
    const base: RelatedOffer = d.related ?? { available: ['grid'], default: 'grid' }
    const perSlot = { ...(base.perSlot ?? {}) }
    if (next === null) delete perSlot[slot.id]
    else {
      const current = perSlot[slot.id] ?? { target: slot.target, available: ['grid'], default: 'grid' }
      perSlot[slot.id] = {
        ...current, ...next,
        // The far entity is DECLARED on every write. The id contains it and the
        // trust boundary refuses to read it back out, because a renamed
        // relationship type would become a silent capability change.
        target: slot.target,
      }
    }
    const related: RelatedOffer = Object.keys(perSlot).length > 0
      ? { ...base, perSlot }
      : (({ perSlot: _drop, ...rest }) => rest)({ ...base, perSlot })
    return { ...d, version: docVersion(d), related }
  })

  const toggleSlotLens = (slot: TableSlotChoice, kind: RelatedPresentation, on: boolean) => {
    const entry = draft.related?.perSlot?.[slot.id]
    const current = (entry?.available ?? ['grid']) as RelatedPresentation[]
    const available = on
      ? RELATED_PRESENTATIONS.filter((k) => k === kind || current.includes(k))
      : current.filter((k) => k !== kind)
    const safe = available.length > 0 ? available : (['grid'] as RelatedPresentation[])
    const wanted = (entry?.default ?? 'grid') as RelatedPresentation
    patchSlot(slot, {
      available: safe,
      // A default that just stopped being offered falls to the first lens that
      // still is, rather than being saved as a value the boundary will refuse.
      default: safe.includes(wanted) ? wanted : safe[0]!,
    })
  }

  /**
   * Choose one section of THIS entity's form for a preview.
   *
   * Removing the last one drops the block entirely rather than storing an empty
   * list: the trust boundary refuses `sections: []` — a preview of nothing is
   * not a preview — and absent means "fall back to the first section", which is
   * a different and legitimate answer.
   */
  const togglePeekSection = (id: string, on: boolean) => setDraft((d) => {
    const current = d.peek?.sections ?? []
    const next = on
      ? sections.filter((s2) => s2.id === id || current.includes(s2.id)).map((s2) => s2.id)
      : current.filter((s2) => s2 !== id)
    if (next.length === 0) { const { peek: _peek, ...rest } = d; return { ...rest, version: docVersion(d) } }
    return { ...d, version: docVersion(d), peek: { sections: next } }
  })

  /**
   * The WHOLE-RECORD workspace contract (#2324).
   *
   * Its own patcher, beside `patchRelated` and never inside it, because the two
   * are different statements about different scopes — the ruling says so in as
   * many words, and a shared patcher is how they would quietly become one
   * setting again.
   *
   * `null` removes the block, which is Reset: the record goes back to the
   * platform floor (both modes, opening in List on Network) rather than being
   * pinned to a copy of today's answer.
   */
  const patchWorkspace = (next: Partial<WorkspaceContract> | null) => setDraft((d) => {
    if (next === null) {
      const { workspace: _workspace, ...rest } = d
      return { ...rest, version: atVersion(rest, 2) }
    }
    const base = d.workspace ?? WORKSPACE_FLOOR
    const merged = { ...base, ...next }
    // LIST IS NOT OPTIONAL — the boundary refuses a workspace without it, so the
    // editor repairs rather than handing the author an error they did not cause.
    // It is the complete accessible fallback and the only mode that can add a
    // relationship.
    const modes: WorkspaceMode[] = merged.modes.includes('list')
      ? merged.modes : [...merged.modes, 'list']
    const defaultMode = modes.includes(merged.defaultMode) ? merged.defaultMode : 'list'
    let graph = merged.graph
    if (graph) {
      const layouts = graph.layouts.length > 0 ? graph.layouts : (['network'] as GraphLayout[])
      graph = {
        ...graph,
        layouts,
        defaultLayout: layouts.includes(graph.defaultLayout) ? graph.defaultLayout : layouts[0]!,
      }
    }
    return {
      ...d,
      version: atVersion(d, 3),
      workspace: { ...merged, modes, defaultMode, ...(graph ? { graph } : {}) },
    }
  })

  const toggleMode = (mode: WorkspaceMode, on: boolean) => {
    const current = draft.workspace?.modes ?? WORKSPACE_FLOOR.modes
    const modes = on
      ? WORKSPACE_MODES.filter((m) => m === mode || current.includes(m))
      : current.filter((m) => m !== mode)
    patchWorkspace({ modes: [...modes] })
  }

  const toggleLayout = (layout: GraphLayout, on: boolean) => {
    const graph = draft.workspace?.graph
    const current = graph?.layouts ?? ([...GRAPH_LAYOUTS] as GraphLayout[])
    const layouts = on
      ? GRAPH_LAYOUTS.filter((l) => l === layout || current.includes(l))
      : current.filter((l) => l !== layout)
    const safe = layouts.length > 0 ? [...layouts] : (['network'] as GraphLayout[])
    const wanted = graph?.defaultLayout ?? 'network'
    patchWorkspace({
      graph: {
        ...(graph ?? { layouts: safe, defaultLayout: 'network' }),
        layouts: safe,
        defaultLayout: safe.includes(wanted) ? wanted : safe[0]!,
      },
    })
  }

  const toggleRelated = (kind: RelatedPresentation, on: boolean) => {
    const current = draft.related?.available ?? (['grid'] as RelatedPresentation[])
    patchRelated({
      available: on
        ? RELATED_PRESENTATIONS.filter((k) => k === kind || current.includes(k))
        : current.filter((k) => k !== kind),
    })
  }

  const patchCard = (next: Partial<TableCardSpec> | null) => setDraft((d) => {
    if (next === null) { const { card: _card, ...rest } = d; return rest }
    const base: TableCardSpec = d.card ?? { primary: fields[0]?.key ?? '' }
    return { ...d, card: { ...base, ...next } }
  })

  const patchQuickCreate = (next: Partial<NonNullable<TablePresentationDocument['quickCreate']>> | null) =>
    setDraft((d) => {
      if (next === null) {
        const { quickCreate: _quickCreate, ...rest } = d
        /* A "New" command opens THIS block's form. Removing the block leaves it
           nothing to open, which the boundary refuses — so the editor removes the
           command with it rather than handing the author an error they did not
           cause (the repair `toggleLens` makes for an orphaned default). */
        return rest.commands
          ? { ...rest, commands: rest.commands.filter((c) => c.action !== 'quickCreate') }
          : rest
      }
      const requestedForm = next.form === undefined ? d.quickCreate?.form : next.form
      const selected = requestedForm
        ? forms.find((form) => form.key === requestedForm)
        : forms.find((form) => form.automatic)
      const admitted = selected?.fields ? new Set(selected.fields) : null
      const required = fields
        .filter((field) => field.required && !field.hasServerDefault && (!admitted || admitted.has(field.key)))
        .map((field) => field.key)
      const base = d.quickCreate ?? { fields: required.length > 0 ? required : fields[0] ? [fields[0].key] : [] }
      const changingForm = Object.prototype.hasOwnProperty.call(next, 'form')
      const requestedFields = next.fields ?? base.fields
      const candidateFields = changingForm && admitted
        ? requestedFields.filter((key) => admitted.has(key))
        : requestedFields
      const reconciled = [...required, ...candidateFields.filter((key) => !required.includes(key))]
      const fallback = selected?.fields?.[0] ?? fields[0]?.key
      return { ...d, quickCreate: {
        ...base, ...next,
        fields: reconciled.length > 0 ? reconciled : fallback ? [fallback] : [],
      } }
    })

  /**
   * The collection's COMMANDS (#2375, grammar version 5).
   *
   * The author SELECTS from governed commands — the two built-in seams and the
   * collection-level verbs this editor was given — and ARRANGES and WORDS them.
   * Nothing here can name an action that was not offered, which is the
   * editor's half of "presentation never grants".
   *
   * `null` removes the block, which is Reset: the table goes back to DERIVING
   * its commands (New where quick create is available, Link existing where
   * linking is) rather than being pinned to a copy of today's answer. The
   * version returns to what the remaining blocks need — and never below what
   * the stored document already said, so switching the block on and off is not
   * an edit.
   */
  const patchCommands = (next: TableCommand[] | null) => setDraft((d) => {
    if (next === null) {
      const { commands: _commands, ...rest } = d
      const stored = value.commands == null ? value.version : 1
      return { ...rest, version: atVersion(rest, stored) }
    }
    return { ...d, version: atVersion(d, 5), commands: next }
  })

  const commandChoices: { key: string; label: string; defaultLabel: string }[] = [
    ...(draft.quickCreate && quickCreateAvailable
      ? [{ key: 'quickCreate', label: 'New (opens quick create)', defaultLabel: 'New' }] : []),
    { key: 'linkExisting', label: 'Link existing', defaultLabel: 'Link existing' },
    ...(commandActionsAvailable
      ? commandActions.map((a) => ({ key: a.key, label: a.label, defaultLabel: a.label })) : []),
  ]
  const actionLabel = (action: string) =>
    commandChoices.find((c) => c.key === action)?.label ?? action

  const addCommand = (choice: { key: string; defaultLabel: string }) => {
    const current = draft.commands ?? []
    const base = choice.key === 'quickCreate' ? 'new' : choice.key === 'linkExisting' ? 'link' : choice.key
    let id = base
    for (let n = 2; current.some((c) => c.id === id); n += 1) id = `${base}-${n}`
    patchCommands([...current, { id, label: choice.defaultLabel, kind: 'secondary', action: choice.key }])
  }

  const setCommandKind = (index: number, kind: TableCommandKind) => {
    const current = [...(draft.commands ?? [])]
    const target = current[index]
    if (!target) return
    if (kind === 'secondary') {
      current[index] = { ...target, kind }
      patchCommands(current)
      return
    }
    /* ONE primary, and it LEADS — the rule both validators hold. Choosing a new
       primary demotes the old one and moves the chosen command to the front,
       rather than composing a list the boundary refuses. */
    const rest = current.filter((_, i) => i !== index)
      .map((c) => (c.kind === 'primary' ? { ...c, kind: 'secondary' as const } : c))
    patchCommands([{ ...target, kind: 'primary' }, ...rest])
  }

  const moveCommand = (index: number, by: -1 | 1) => {
    const current = [...(draft.commands ?? [])]
    const to = index + by
    if (to < 0 || to >= current.length) return
    ;[current[index], current[to]] = [current[to]!, current[index]!]
    patchCommands(current)
  }

  const fieldOptions = [{ value: NONE, label: NONE }, ...fields.map((f) => ({ value: f.key, label: f.label }))]
  const offersCards = draft.availablePresentations.includes('cards')

  return (
    <section className={s.composer} data-testid={testId} aria-label="Table presentation">
      <div className={s.toolbar}>
        <div>
          <strong>{layerLabel}</strong>
          {provenance?.scope && (
            /* An author editing a tenant default over a solution one must be
               able to see that, or "reset" means nothing to them. */
            <p data-testid={`${testId}-provenance`}>
              Currently from the {provenance.scope} layer
              {provenance.source === 'fallback' ? ' — nothing is declared yet' : ''}
            </p>
          )}
        </div>
        {layerControl}
      </div>

      <fieldset className={s.group}>
        <legend>Views this table offers</legend>
        <p className={s.hint}>
          People choose between these. The offer is the deployment’s; a surface that
          cannot draw one says so and shows the next it can.
        </p>
        {TABLE_PRESENTATIONS.map((kind) => {
          const on = draft.availablePresentations.includes(kind)
          const drawable = supportedSet.has(kind)
          return (
            <label key={kind} className={s.row} data-testid={`${testId}-lens-${kind}`}>
              <input
                type="checkbox" checked={on} disabled={busy || (!drawable && !on)}
                onChange={(e) => toggleLens(kind, e.currentTarget.checked)}
              />
              <span>{LENS_LABEL[kind]}</span>
              {!drawable && (
                /* Marked, never hidden — see `supported`'s doc comment. */
                <span className={s.note} data-testid={`${testId}-unsupported-${kind}`}>
                  not available on this surface
                </span>
              )}
            </label>
          )
        })}
      </fieldset>

      <fieldset className={s.group} data-testid={`${testId}-related`}>
        <legend>Related records</legend>
        <p className={s.hint}>
          How this entity appears when it is related to another record. A person may
          still choose a different one; this is what they get before they do.
        </p>
        <label className={s.row} data-testid={`${testId}-related-enabled`}>
          <input
            type="checkbox" checked={draft.related != null} disabled={busy}
            onChange={(e) => patchRelated(e.currentTarget.checked ? {} : null)}
          />
          <span>Choose how related records of this type are shown</span>
        </label>
        {draft.related && (<>
          {RELATED_PRESENTATIONS.map((kind) => {
            const on = draft.related!.available.includes(kind)
            // ELIGIBILITY, and it is about the DATA rather than about the host:
            // a map can only draw an entity that declares where its coordinates
            // are. The recipe is shipped by the module that owns the entity, so
            // an author sees why rather than a control that silently does
            // nothing.
            const eligible = kind !== 'map' || !!draft.map
            return (
              <label key={kind} className={s.row} data-testid={`${testId}-related-${kind}`}>
                <input
                  type="checkbox" checked={on} disabled={busy}
                  onChange={(e) => toggleRelated(kind, e.currentTarget.checked)}
                />
                <span>{RELATED_LABEL[kind]}</span>
                {!eligible && (
                  <span className={s.note} data-testid={`${testId}-related-ineligible-${kind}`}>
                    this entity declares no coordinates, so it cannot be the default
                  </span>
                )}
              </label>
            )
          })}
          <Field orientation="row" label="Related records open as">
            <Select
              value={draft.related.default} disabled={busy}
              data-testid={`${testId}-related-default`}
              onValueChange={(v) => patchRelated({ default: v as RelatedPresentation })}
              /* The DEFAULT is where eligibility binds, and it binds here rather
                 than on the checkbox above on purpose. A workspace draws OTHER
                 entities' records, so an entity with no coordinates of its own
                 may legitimately OFFER a map — what it may not do is promise one
                 by default, because no data could ever rescue that promise. The
                 database refuses the same thing in the same place. */
              options={draft.related.available
                .filter((kind) => kind !== 'map' || !!draft.map)
                .map((kind) => ({ value: kind, label: RELATED_LABEL[kind] }))}
            />
          </Field>
          {/* The PREVIEW is the real control, not a picture of one: the same
              segmented control the record page renders, with the same options in
              the same order and this document's default selected. A drawn
              imitation could agree with the document and disagree with the page. */}
          <div className={s.details} data-testid={`${testId}-related-preview`}>
            <span className={s.hint}>What a person will see</span>
            <SegmentedControl<RelatedPresentation>
              value={draft.related.default}
              onChange={(v) => patchRelated({ default: v })}
              options={draft.related.available.map((kind) => ({ value: kind, label: RELATED_LABEL[kind] }))}
              label="Show connections as" size="sm" />
          </div>
        </>)}
      </fieldset>

      {/* ── the WHOLE-RECORD relationship workspace (#2324) ─────────────────
          A SIBLING of the block above, and drawn as one: "how do I read the
          addresses on this record" and "how does this record's whole
          relationship network open" are different questions with different
          scopes, and the ruling forbids reinterpreting either as the other. A
          shared fieldset would have made confusing them the natural reading. */}
      <fieldset className={s.group} data-testid={`${testId}-workspace`}>
        <legend>Relationship workspace</legend>
        <p className={s.hint}>
          How this record&rsquo;s relationships open as a whole — as the list of
          sections, or as one graph of everything it is connected to. A person may
          still choose differently; this is what they get before they do.
        </p>
        <label className={s.row} data-testid={`${testId}-workspace-enabled`}>
          <input
            type="checkbox" checked={draft.workspace != null} disabled={busy}
            onChange={(e) => patchWorkspace(e.currentTarget.checked ? {} : null)}
          />
          <span>Choose how this record&rsquo;s relationships open</span>
        </label>
        {draft.workspace && (<>
          {WORKSPACE_MODES.map((mode) => {
            const on = draft.workspace!.modes.includes(mode)
            return (
              <label key={mode} className={s.row} data-testid={`${testId}-workspace-mode-${mode}`}>
                <input
                  type="checkbox" checked={on} disabled={busy || mode === 'list'}
                  onChange={(e) => toggleMode(mode, e.currentTarget.checked)}
                />
                <span>{MODE_LABEL[mode]}</span>
                {/* Stated rather than left for an author to discover by finding
                    the control does nothing. The database refuses a workspace
                    without List, and for a reason a person can act on: it is the
                    complete accessible fallback and the only mode that can add a
                    relationship. */}
                {mode === 'list' && (
                  <span className={s.note} data-testid={`${testId}-workspace-mode-list-note`}>
                    always available — it is the accessible fallback, and the only
                    mode that can add a relationship
                  </span>
                )}
              </label>
            )
          })}
          <Field orientation="row" label="Relationships open in">
            <Select
              value={draft.workspace.defaultMode} disabled={busy}
              data-testid={`${testId}-workspace-default-mode`}
              onValueChange={(v) => patchWorkspace({ defaultMode: v as WorkspaceMode })}
              options={draft.workspace.modes.map((m) => ({ value: m, label: MODE_LABEL[m] }))}
            />
          </Field>

          {draft.workspace.modes.includes('graph') && (<>
            <p className={s.hint}>
              Inside the graph, how the same records and relationships are placed.
            </p>
            {GRAPH_LAYOUTS.map((layout) => {
              const offered = draft.workspace!.graph?.layouts ?? ([...GRAPH_LAYOUTS] as GraphLayout[])
              return (
                <label key={layout} className={s.row}
                  data-testid={`${testId}-workspace-layout-${layout}`}>
                  <input
                    type="checkbox" checked={offered.includes(layout)} disabled={busy}
                    onChange={(e) => toggleLayout(layout, e.currentTarget.checked)}
                  />
                  <span>{LAYOUT_LABEL[layout]}</span>
                </label>
              )
            })}
            <Field orientation="row" label="The graph is laid out as">
              <Select
                value={draft.workspace.graph?.defaultLayout ?? 'network'} disabled={busy}
                data-testid={`${testId}-workspace-default-layout`}
                onValueChange={(v) => patchWorkspace({
                  graph: {
                    ...(draft.workspace!.graph
                      ?? { layouts: [...GRAPH_LAYOUTS] as GraphLayout[], defaultLayout: 'network' }),
                    defaultLayout: v as GraphLayout,
                  },
                })}
                options={(draft.workspace.graph?.layouts ?? ([...GRAPH_LAYOUTS] as GraphLayout[]))
                  .map((layout) => ({ value: layout, label: LAYOUT_LABEL[layout] }))}
              />
            </Field>
            {/* Hops, not coordinates. §8 of the ruling: a hand-arranged position
                is not platform data, and nothing here can state one. The MAXIMUM
                is narrowed here and is bounded by the deployment's own ceiling —
                `core.graph_max_depth`, the same one the traversal door enforces —
                which the database checks because the browser holds no settings
                table and a guess here would disagree with the trigger. */}
            <Field orientation="row" label="Opens showing">
              <Select
                value={String(draft.workspace.graph?.initialDepth ?? 1)} disabled={busy}
                data-testid={`${testId}-workspace-initial-depth`}
                onValueChange={(v) => patchWorkspace({
                  graph: {
                    ...(draft.workspace!.graph
                      ?? { layouts: [...GRAPH_LAYOUTS] as GraphLayout[], defaultLayout: 'network' }),
                    initialDepth: Number(v),
                  },
                })}
                options={[1, 2, 3].map((n) => ({
                  value: String(n), label: n === 1 ? 'direct relationships' : `${n} steps out`,
                }))}
              />
            </Field>
          </>)}

          {/* The PREVIEW is the real control, not a picture of one — the same
              reasoning the related block states: a drawn imitation could agree
              with the document and disagree with the page. */}
          <div className={s.details} data-testid={`${testId}-workspace-preview`}>
            <span className={s.hint}>What a person will see</span>
            <SegmentedControl<WorkspaceMode>
              value={draft.workspace.defaultMode}
              onChange={(v) => patchWorkspace({ defaultMode: v })}
              options={draft.workspace.modes.map((m) => ({ value: m, label: MODE_LABEL[m] }))}
              label="Show relationships as" size="sm" />
          </div>
        </>)}
      </fieldset>

      {/* ── which sections a preview of THIS entity shows ───────────────────
          #2281: the recipe names section IDS of this entity's own canonical
          form — the identities core 2026091520 made declared and stored. The
          choices are the sections the host resolved, so nothing is typed and
          nothing is inferred from a name.

          With none selected the renderer falls back to the first visible,
          non-empty section in the resolved order, which is stated here rather
          than left for a reader to discover. */}
      {sections.length > 0 && (
        <fieldset className={s.group} data-testid={`${testId}-peek`}>
          <legend>Preview sections</legend>
          <p className={s.hint}>
            What somebody sees when a record of this type is previewed in place.
            Choose none and the preview shows this form’s first section.
          </p>
          {sections.map((section) => {
            const chosen = draft.peek?.sections?.includes(section.id) ?? false
            return (
              <label key={section.id} className={s.row}
                data-testid={`${testId}-peek-${section.id}`}>
                <input
                  type="checkbox" checked={chosen} disabled={busy}
                  onChange={(e) => togglePeekSection(section.id, e.currentTarget.checked)}
                />
                <span>{section.label}</span>
              </label>
            )
          })}
        </fieldset>
      )}

      {/* ── per relationship ────────────────────────────────────────────────
          Point 6 of the owner correction. The fieldset above says how THIS
          entity reads when something else is related to it; this one says how
          each of this entity's OWN relationships reads on its record page.

          They are different questions and used to be one, which is why an
          organisation could not show its addresses on a map and its people as a
          list. Absent slots ⇒ this surface is not authoring a source, and the
          section is not drawn at all rather than drawn empty. */}
      {slots.length > 0 && (
        <fieldset className={s.group} data-testid={`${testId}-slots`}>
          <legend>Per relationship</legend>
          <p className={s.hint}>
            How each of this record's relationships opens. Two relationships reaching
            the same kind of record may differ. A relationship you do not set here
            inherits the answer below it, and keeps inheriting when that answer changes.
          </p>
          {slots.map((slot) => {
            const entry = draft.related?.perSlot?.[slot.id]
            const authored = entry != null
            const available = (entry?.available ?? []) as RelatedPresentation[]
            return (
              <div key={slot.id} className={s.details} data-testid={`${testId}-slot-${slot.id}`}>
                <span className={s.hint}>
                  {slot.label}
                  {slot.targetLabel ? ` · ${slot.targetLabel}` : ''}
                  {/* PROVENANCE, in the only words that help: an author looking
                      at a default they did not set needs to know whether it is
                      theirs to change here or inherited from somewhere else. */}
                  {authored ? ' · set here' : ' · inherited'}
                </span>
                {RELATED_PRESENTATIONS.map((kind) => {
                  // ELIGIBILITY is the TARGET's property. A map can only draw an
                  // entity that declares where its coordinates are, and that
                  // recipe is shipped by the module that owns the far entity —
                  // so an author sees why rather than a control that silently
                  // does nothing.
                  /* Two lenses need something from the TARGET, and they are
                     asked separately because they are different questions: map
                     needs a coordinate recipe, peek needs a canonical readable
                     form. Anything else is eligible everywhere. */
                  const eligible = kind === 'map' ? slot.mapCapable === true
                    : kind === 'peek' ? slot.peekCapable === true
                      : true
                  return (
                    <label key={kind} className={s.row}
                      data-testid={`${testId}-slot-${slot.id}-${kind}`}>
                      <input
                        type="checkbox" disabled={busy || !eligible}
                        checked={available.includes(kind)}
                        onChange={(e) => toggleSlotLens(slot, kind, e.currentTarget.checked)}
                      />
                      <span>{RELATED_LABEL[kind]}</span>
                      {!eligible && (
                        <span className={s.note}
                          data-testid={`${testId}-slot-${slot.id}-ineligible-${kind}`}>
                          {kind === 'map'
                            ? `${slot.targetLabel ?? slot.target} declares no coordinates`
                            : `${slot.targetLabel ?? slot.target} resolves no form to preview`}
                        </span>
                      )}
                    </label>
                  )
                })}
                {authored && (<>
                  <Field orientation="row" label="Opens as">
                    <Select
                      value={entry.default ?? 'grid'} disabled={busy}
                      data-testid={`${testId}-slot-${slot.id}-default`}
                      onValueChange={(v) => patchSlot(slot, { default: v as RelatedPresentation })}
                      options={available.map((kind) => ({ value: kind, label: RELATED_LABEL[kind] }))}
                    />
                  </Field>
                  {/* The PREVIEW is the real control, not a picture of one: the
                      same segmented control the record page renders, with the
                      same options in the same order. A drawn imitation could
                      agree with this document and disagree with the page. */}
                  {available.length > 1 && (
                    <SegmentedControl<RelatedPresentation>
                      value={(entry.default ?? 'grid') as RelatedPresentation}
                      onChange={(v) => patchSlot(slot, { default: v })}
                      options={available.map((kind) => ({ value: kind, label: RELATED_LABEL[kind] }))}
                      label={`Show ${slot.label} as`} size="sm"
                      testId={`${testId}-slot-${slot.id}-preview`} />
                  )}
                  <button type="button" className={s.linkish} disabled={busy}
                    data-testid={`${testId}-slot-${slot.id}-reset`}
                    onClick={() => patchSlot(slot, null)}>
                    Reset to inherited
                  </button>
                </>)}
              </div>
            )
          })}
        </fieldset>
      )}

      <fieldset className={s.group} data-testid={`${testId}-quick-create`}>
        <legend>Quick create</legend>
        <label className={s.row}>
          <input
            type="checkbox" checked={draft.quickCreate != null}
            disabled={busy || fields.length === 0 || (!quickCreateAvailable && !draft.quickCreate)}
            data-testid={`${testId}-quick-create-enabled`}
            onChange={(e) => patchQuickCreate(e.currentTarget.checked ? {} : null)}
          />
          <span>Offer a compact create form from this table</span>
          {!quickCreateAvailable && <span className={s.note}>not available on this surface</span>}
        </label>
        {draft.quickCreate && (<>
          <Field orientation="row" label="Governed form">
            <Select
              value={draft.quickCreate.form ?? NONE} disabled={busy}
              data-testid={`${testId}-quick-create-form`}
              onValueChange={(v) => patchQuickCreate({ form: v === NONE ? undefined : v })}
              options={[{ value: NONE, label: 'Automatic' }, ...forms.map((form) => ({ value: form.key, label: form.label }))]}
            />
          </Field>
          <div className={s.details}>
            <span className={s.hint}>Fields, in display order</span>
            {fields.map((field) => {
              const on = draft.quickCreate?.fields.includes(field.key) ?? false
              const admitted = selectedQuickFields?.has(field.key) ?? true
              const required = Boolean(field.required && !field.hasServerDefault && admitted)
              return <label key={field.key} className={s.row}>
                <input
                  type="checkbox" checked={on} disabled={busy || required || (!admitted && !on)}
                  data-testid={`${testId}-quick-create-field-${field.key}`}
                  onChange={(e) => patchQuickCreate({
                    fields: e.currentTarget.checked
                      ? [...(draft.quickCreate?.fields ?? []), field.key]
                      : (draft.quickCreate?.fields ?? []).filter((key) => key !== field.key),
                  })}
                />
                <span>{field.label}{field.required
                  ? field.hasServerDefault ? ' (required; server-filled when blank)' : ' (required)'
                  : ''}{!admitted ? ' (not on selected form)' : ''}</span>
              </label>
            })}
          </div>
        </>)}
      </fieldset>

      <fieldset className={s.group}>
        <legend>Defaults</legend>
        <Field orientation="row" label="Opens as">
          <Select
            value={draft.defaultPresentation} disabled={busy}
            data-testid={`${testId}-default`}
            onValueChange={(v) => patch({ defaultPresentation: v as TablePresentation })}
            options={draft.availablePresentations.map((k) => ({ value: k, label: LENS_LABEL[k] }))}
          />
        </Field>
        <Field orientation="row" label="Command buttons">
          <Select
            value={draft.commandBar} disabled={busy}
            data-testid={`${testId}-commandbar`}
            onValueChange={(v) => patch({ commandBar: v as TableCommandBarDefault })}
            options={COMMAND_BAR}
          />
        </Field>
      </fieldset>

      {/* ── the collection's COMMANDS (#2375) ───────────────────────────────
          Which governed commands sit beside the table's name, in what order
          and in what words. Off is INHERITANCE — the table derives New and
          Link existing from what the host can do — which is a different
          state from an authored empty list. */}
      <fieldset className={s.group} data-testid={`${testId}-commands`}>
        <legend>Commands</legend>
        <p className={s.hint}>
          The buttons beside this table&rsquo;s name. They choose and word commands
          that are already governed — they grant nothing. Left unset, the table
          offers New and Link existing wherever it can.
        </p>
        <label className={s.row} data-testid={`${testId}-commands-enabled`}>
          <input
            type="checkbox" checked={draft.commands != null} disabled={busy}
            onChange={(e) => patchCommands(e.currentTarget.checked
              ? (draft.quickCreate && quickCreateAvailable ? [{ ...DERIVED_QUICK_CREATE_COMMAND }] : [])
              : null)}
          />
          <span>Choose this table&rsquo;s commands</span>
        </label>
        {draft.commands != null && (<>
          {draft.commands.length === 0 && (
            <p className={s.hint} data-testid={`${testId}-commands-empty`}>
              No commands — this table offers none beside its name.
            </p>
          )}
          {draft.commands.map((command, index) => {
            const list = draft.commands!
            const primaryAbove = index > 0 && list[index - 1]!.kind === 'primary'
            return (
              <div key={command.id} className={s.details} data-testid={`${testId}-command-${command.id}`}>
                <span className={s.hint}>Runs {actionLabel(command.action)}</span>
                <Field orientation="row" label="Label">
                  <Input
                    value={command.label} disabled={busy}
                    data-testid={`${testId}-command-${command.id}-label`}
                    onChange={(e) => {
                      const label = e.currentTarget.value
                      patchCommands(list.map((c, i) => (i === index ? { ...c, label } : c)))
                    }}
                  />
                </Field>
                <Field orientation="row" label="Style">
                  <Select
                    value={command.kind} disabled={busy}
                    data-testid={`${testId}-command-${command.id}-kind`}
                    onValueChange={(v) => setCommandKind(index, v as TableCommandKind)}
                    options={[
                      { value: 'primary', label: 'Primary — the main command, drawn first' },
                      { value: 'secondary', label: 'Secondary' },
                    ]}
                  />
                </Field>
                <div className={s.row}>
                  {/* A primary LEADS, so it is not moved and nothing moves above it. */}
                  <Button variant="ghost" size="sm"
                    disabled={busy || index === 0 || command.kind === 'primary' || primaryAbove}
                    data-testid={`${testId}-command-${command.id}-up`}
                    onClick={() => moveCommand(index, -1)}>Move up</Button>
                  <Button variant="ghost" size="sm"
                    disabled={busy || index === list.length - 1 || command.kind === 'primary'}
                    data-testid={`${testId}-command-${command.id}-down`}
                    onClick={() => moveCommand(index, 1)}>Move down</Button>
                  <Button variant="ghost" size="sm" disabled={busy}
                    data-testid={`${testId}-command-${command.id}-remove`}
                    onClick={() => patchCommands(list.filter((_, i) => i !== index))}>Remove</Button>
                </div>
              </div>
            )
          })}
          <div className={s.details} data-testid={`${testId}-commands-add`}>
            <span className={s.hint}>Add a command</span>
            {commandChoices.map((choice) => (
              <Button key={choice.key} variant="ghost" size="sm" disabled={busy}
                data-testid={`${testId}-commands-add-${choice.key}`}
                onClick={() => addCommand(choice)}>{choice.label}</Button>
            ))}
          </div>
        </>)}
      </fieldset>

      {offersCards && (
        <fieldset className={s.group} data-testid={`${testId}-card`}>
          <legend>What a card shows</legend>
          <p className={s.hint}>
            A card is a chosen summary. Anything not named here is left off it —
            that is what makes it different from the grid.
          </p>
          <Field orientation="row" label="Title">
            <Select
              value={draft.card?.primary ?? NONE} disabled={busy}
              data-testid={`${testId}-card-primary`}
              onValueChange={(v) => patchCard(v === NONE ? null : { primary: v })}
              options={fieldOptions}
            />
          </Field>
          {(['badge', 'accent', 'icon'] as const).map((slot) => (
            <Field orientation="row" label={slot[0]!.toUpperCase() + slot.slice(1)} key={slot}>
              <Select
                value={draft.card?.[slot] ?? NONE} disabled={busy || !draft.card}
                data-testid={`${testId}-card-${slot}`}
                onValueChange={(v) => patchCard({ [slot]: v === NONE ? undefined : v })}
                options={fieldOptions}
              />
            </Field>
          ))}
          <div className={s.details}>
            <span className={s.hint}>Details</span>
            {fields.map((f) => {
              const on = draft.card?.details?.includes(f.key) ?? false
              return (
                <label key={f.key} className={s.row}>
                  <input
                    type="checkbox" checked={on} disabled={busy || !draft.card}
                    data-testid={`${testId}-card-detail-${f.key}`}
                    onChange={(e) => patchCard({
                      details: e.currentTarget.checked
                        ? [...(draft.card?.details ?? []), f.key]
                        : (draft.card?.details ?? []).filter((k) => k !== f.key),
                    })}
                  />
                  <span>{f.label}</span>
                </label>
              )
            })}
          </div>
          {actions.length > 0 && <div className={s.details} data-testid={`${testId}-card-actions`}>
            <span className={s.hint}>Governed actions</span>
            {actions.map((action) => {
              const on = draft.card?.actions?.includes(action.key) ?? false
              return <label key={action.key} className={s.row}>
                <input
                  type="checkbox" checked={on} disabled={busy || !draft.card}
                  data-testid={`${testId}-card-action-${action.key}`}
                  onChange={(e) => patchCard({
                    actions: e.currentTarget.checked
                      ? [...(draft.card?.actions ?? []), action.key]
                      : (draft.card?.actions ?? []).filter((key) => key !== action.key),
                  })}
                />
                <span>{action.label}</span>
              </label>
            })}
          </div>}
          {!cardActionsAvailable && (draft.card?.actions?.length ?? 0) > 0 && (
            <div className={s.details} data-testid={`${testId}-card-actions-unavailable`}>
              <span className={s.note}>Actions are not available on this surface.</span>
              <Button variant="ghost" disabled={busy} onClick={() => patchCard({ actions: [] })}>
                Remove card actions
              </Button>
            </div>
          )}
        </fieldset>
      )}

      {allIssues.length > 0 && (
        <div className={s.issues} role="status" data-testid={`${testId}-issues`}>
          <strong>This can’t be saved yet</strong>
          <ul>{allIssues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
        </div>
      )}

      <ActionBar
        testId={`${testId}-actions`}
        label="Table presentation"
        status={dirty ? 'Unsaved changes' : undefined}
        secondaryActions={<>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
          {onReset && (
            <Button
              variant="ghost" onClick={onReset} disabled={busy}
              data-testid={`${testId}-reset`}
            >Reset to the layer beneath</Button>
          )}
        </>}
        primaryAction={
          <Button
            onClick={() => onSave(draft)}
            /* Disabled on ISSUES, not on cleanliness: an author who has fixed a
               document that was already invalid must be able to save it even
               though they changed nothing this session. */
            disabled={busy || allIssues.length > 0 || !dirty}
            data-testid={`${testId}-save`}
          >Save</Button>
        }
      />
    </section>
  )
}

/* Re-declared beside the component whose props are written in it, so a host
   can type the data it hands this component from `@solum/ui` alone (#2406,
   design-sync kit pass: "hosts need @solum/experience to type their data").
   An alias, not a copy — the shape stays Experience's. */
export type TablePresentationDocument = GovernedTablePresentationDocument
